import dayjs from "dayjs";
// The labels below are Polish; do not rely on the host app having registered the locale.
import "dayjs/locale/pl";

import { ChallengeVerification } from "../../entities/Challenge";
import { ExerciseRecordingStatus } from "../../entities/ExerciseRecording";

/**
 * Read model behind the trainee-facing challenge progress (TRE-1828) — the client half of
 * `ChallengeScoreCalculator`. Kept next to the calculator, and shared by mobile and client-web,
 * because the two must agree on what "done" means: a card claiming a task is complete while the
 * engine still owes it a date is the bug this file exists to prevent.
 */

/** Lifecycle rank (scheduled → recorded → rated); pending/archived never count. */
const RECORDING_RANK: Record<string, number> = {
  [ExerciseRecordingStatus.PENDING]: 0,
  [ExerciseRecordingStatus.ARCHIVED]: 0,
  [ExerciseRecordingStatus.SCHEDULED]: 1,
  [ExerciseRecordingStatus.RECORDED]: 2,
  [ExerciseRecordingStatus.RATED]: 3,
};

export const recordingRank = (status: ExerciseRecordingStatus | string): number => RECORDING_RANK[status] ?? 0;

/** Rank a date must reach to count — mirrors ChallengeScoreCalculator.qualifyingRecordingDays(). */
const thresholdRank = (verification: ChallengeVerification): number =>
  verification === ChallengeVerification.TrainerApproval
    ? recordingRank(ExerciseRecordingStatus.RATED)
    : recordingRank(ExerciseRecordingStatus.RECORDED);

/** One scheduled date of a recording task, as the screens consume it. */
export type RecordingDateLike = { date: string | null; status: ExerciseRecordingStatus };
type RecordingLike = { dates: RecordingDateLike[] };

/**
 * done = counted toward the task; pending = video uploaded, trainer has not rated it yet;
 * today = due now; missed = the day passed without a video; upcoming = still ahead.
 */
export type RecordingDateState = "done" | "pending" | "today" | "missed" | "upcoming";

export type RecordingProgress = {
  /** Scheduled dates the trainee owes. */
  total: number;
  /** Of those, dates that count for the task (rated for trainerApproval, recorded otherwise). */
  done: number;
  /** Dates whose day has already arrived (today included — it is owed by tonight). */
  due: number;
  /** Uploaded videos among the due dates — the honest "Nagrane 1/2 do dziś" numerator. */
  uploadedDue: number;
  /** Uploaded but still waiting for the trainer's rating (trainerApproval tasks only). */
  awaitingRating: number;
  /** Per-date states, in the same order as `dates` — the dots row. */
  states: RecordingDateState[];
};

/**
 * Progress of a recording task against its scheduled dates. Two thresholds, on purpose: the
 * trainee's side of the deal is the upload, so "Nagrane" counts videos the moment they land, while
 * `done` (what the green check and the score need) waits for the trainer's rating on
 * trainerApproval tasks — otherwise an uploaded video would read as a missed day. The label
 * divides by the dates due so far: a twelve-date task measured against all twelve reads as 8% on
 * day one even when the trainee is perfectly up to date.
 */
export const recordingProgress = (
  verification: ChallengeVerification,
  recording: RecordingLike | undefined,
  today: string = dayjs().format("YYYY-MM-DD"),
): RecordingProgress => {
  const threshold = thresholdRank(verification);
  const uploadedRank = recordingRank(ExerciseRecordingStatus.RECORDED);
  const dates = recording?.dates ?? [];
  let done = 0;
  let due = 0;
  let uploadedDue = 0;
  let awaitingRating = 0;

  const states = dates.map<RecordingDateState>((entry) => {
    const rank = recordingRank(entry.status);
    const counted = rank >= threshold;
    const uploaded = rank >= uploadedRank;
    // A row with no scheduled day (legacy) is owed immediately — it has no future to wait for.
    const isDue = !entry.date || entry.date <= today;
    if (counted) {
      done += 1;
    }
    if (uploaded && !counted) {
      awaitingRating += 1;
    }
    if (isDue) {
      due += 1;
      if (uploaded) {
        uploadedDue += 1;
      }
    }
    if (counted) {
      return "done";
    }
    if (uploaded) {
      return "pending";
    }
    if (!isDue) {
      return "upcoming";
    }
    return entry.date === today ? "today" : "missed";
  });

  return { total: dates.length, done, due, uploadedDue, awaitingRating, states };
};

/**
 * Green-check condition for a recording task: every scheduled date has a recording at/above the
 * task's threshold (trainerApproval ⇒ rated; else ⇒ recorded). One of twelve is progress, not done —
 * mirrors the scoring engine's completion rule for multi-date tasks.
 */
export const recordingTaskCompleted = (progress: RecordingProgress): boolean =>
  progress.total > 0 && progress.done >= progress.total;

/** "termin" / "terminy" / "terminów" — Polish plural for the date count. */
const terminNoun = (count: number): string => {
  if (count === 1) {
    return "termin";
  }
  const mod10 = count % 10;
  const mod100 = count % 100;
  return mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "terminy" : "terminów";
};

/** "Nagrane 1/2 do dziś · 12 terminów, najbliższy 6 sie" — the multi-date task subtitle. */
export const recordingProgressLabel = (progress: RecordingProgress, nextScheduledAt: string | null): string => {
  const next = nextScheduledAt ? `, najbliższy ${dayjs(nextScheduledAt).locale("pl").format("D MMM")}` : "";
  if (progress.due === 0) {
    return `${progress.total} ${terminNoun(progress.total)} przed Tobą${next}`;
  }
  // The rating is the trainer's move, not the trainee's — name it instead of hiding the video.
  const awaiting = progress.awaitingRating > 0 ? ` · ${progress.awaitingRating} czeka na ocenę` : "";
  return `Nagrane ${progress.uploadedDue}/${progress.due} do dziś · ${progress.total} ${terminNoun(progress.total)}${awaiting}${next}`;
};

/** The snapshot fields the progress bar reads (units, with the task pair as fallback). */
type ProgressSnapshotLike = {
  completedTasks: number;
  totalTasks: number;
  completedUnits: number;
  totalUnits: number;
};

export type ChallengeProgress = {
  completed: number;
  total: number;
  percent: number;
  /** Units differ from tasks — a multi-date recording task is in play. */
  countsDates: boolean;
};

/**
 * Progress of a whole challenge. Units count each scheduled date of a multi-date recording task
 * separately, so the bar moves per recording instead of sitting at 0% until the last one.
 * Snapshots written before the units migration have `totalUnits = 0` — those fall back to the task
 * pair until the recompute cron catches up.
 */
export const challengeProgress = (
  snapshot: ProgressSnapshotLike | null | undefined,
  fallbackTotal = 0,
): ChallengeProgress => {
  const usesUnits = (snapshot?.totalUnits ?? 0) > 0;
  const completed = (usesUnits ? snapshot?.completedUnits : snapshot?.completedTasks) ?? 0;
  const total = (usesUnits ? snapshot?.totalUnits : snapshot?.totalTasks) || fallbackTotal;
  return {
    completed,
    total,
    percent: total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0,
    countsDates: usesUnits && snapshot?.totalUnits !== snapshot?.totalTasks,
  };
};

/** "Ukończono 3/5 zadań", or "Ukończono 1/12" once dates rather than tasks are being counted. */
export const challengeProgressLabel = (progress: ChallengeProgress): string =>
  progress.countsDates
    ? `Ukończono ${progress.completed}/${progress.total}`
    : `Ukończono ${progress.completed}/${progress.total} zadań`;
